import * as React from 'react';
import API, { GenericAddResponse } from "../../common/API";
import { useAuth } from "react-oidc-context";
import { useNavigate, useParams } from "react-router-dom";
import { FindResponse } from '../../common/Type';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UserCourseDTO, CourseStatus, LessonType } from '../../common/TrainingTypes';
import { LinkContainer } from 'react-router-bootstrap';
import { Button, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

export function CourseOverviewComponent() {
	const navigate = useNavigate()
    const auth = useAuth()
    const { t } = useTranslation()
	const { id } = useParams();
    const [course, setCourse] = React.useState<UserCourseDTO>();

    const [showCourseWarning, setShowCourseWarning] = React.useState<boolean>(false);

	const startCourse = async () => {
		const response: GenericAddResponse | null = await API.post("/api/v1/training/courses/" + id, {}, auth);
		if (response == null || course == null) {
			return;
		}

		if (course.lessons != null && course.lessons.length > 0) {
			navigate("/training/course/" + id + "/" + course.lessons[0].id);
		}
    }

    const resumeCourse = async () => {
        if (course != null && course.lessons != null && course.lessons.length > 0) {
            navigate("/training/course/" + id + "/" + course.lessons[0].id);
        }
    }

	React.useEffect(() => {
		const getCourse = async () => {
			const response: FindResponse<UserCourseDTO> | null = await API.get("/api/v1/training/courses/" + id, auth);
			if (response != null) {
				setCourse(response.result)
			}
		}

		getCourse()
	}, [auth, id])

	return (
		<>
			<h1 className="mb-3">
                <FontAwesomeIcon icon="graduation-cap" className="me-2" />
                <LinkContainer to="/training/courses" style={{ cursor: 'pointer' }}>
                    <span>{t('training:training')}</span>
                </LinkContainer>
                <FontAwesomeIcon icon="chevron-right" className="mx-2" />
				{course?.name}
			</h1>
			<div className="row">
				<div className="col-md-3 col-lg-3">
					<div className="card">
                        <img className="card-img-top img-responsive" src={course?.thumbnail} alt="Course"></img>
                        <div className="card-header d-flex justify-content-between">
                            <h5 className="card-title mb-0">{course?.category}</h5>
                            <div>
                                <FontAwesomeIcon icon="tags" className="me-1"></FontAwesomeIcon>
                                {course?.tags?.map((tag: string) => {
                                    return (
                                        <span key={tag} className="badge bg-secondary me-2">{tag}</span>
                                    )
                                })}
                            </div>
                        </div>
						<div className="card-body text-center">
                            <p className="mb-0">{course?.description}</p>
						</div>
					</div>
				</div>
				<div className="col-md-5 col-lg-5">
					<div className="card">
						<div className="card-header">
                            <h5 className="card-title mb-0">{course?.lessons?.length} {t('training:lessons')}</h5>
						</div>
						<div className="card-body">
							<ul className="timeline mt-2 mb-0">
								{course?.lessons?.map((e) => {
									return (
                                        <li className={"timeline-item " + (e.quizEnd != null ? "done" : e.startedOn == null ? "inactive" : "")} key={e.id}>
                                            <LinkContainer to={"/training/course/" + course.id + "/" + e.id}>
                                                <a>{e.name}</a>
                                            </LinkContainer>
                                            <span className="float-end text-muted text-sm">
                                                {e.quizEnd != null ? <span className="badge bg-success">{e.quizPercent}%</span> : <></>}
                                                {e.quizEnd == null && e.duration > 0 ? <span>{e.duration}min</span>: <></>}
                                            </span>
                                            <p className="mb-0">{e.description}</p>

                                            <ol>
                                                {e.steps.map((e) => {
                                                    return (
                                                        <li key={e.id}>{e.name} &bull; {LessonType[e.type]}</li>
                                                    )
                                                }) }
                                            </ol>
										</li>
									)
								})}
							</ul>
						</div>
					</div>
				</div>
				<div className="col-md-4 col-lg-4">
					<div className="card">
						<div className="card-header">
                            <h5 className="card-title mb-0">{t('training:courseStats')}</h5>
						</div>
						<div className="card-body">
							<table className="table table-sm my-2">
                                <tbody>
                                    <tr>
                                        <th>{t('training:category')}</th>
                                        <td>{course?.category}</td>
                                    </tr>
                                    {course?.prerequisiteId ? <>
                                        <tr>
                                            <th className="align-middle">{t('training:prerequisiteCourse')}</th>
                                            <td>
                                                <FontAwesomeIcon className={(course.prerequisiteComplete ? "text-success":"text-danger")} icon={(course.prerequisiteComplete ? "check" : "info-circle")}></FontAwesomeIcon>
                                                    
                                                <LinkContainer to={"/training/course/" + course.prerequisiteId}><button type="button" className="btn btn-link">{course.prerequisiteName}</button></LinkContainer>
                                            </td>
                                        </tr>
                                    </> : <></>}
                                    <tr>
                                        <th>Status</th>
                                        <td>
                                            {course?.status === CourseStatus.New ? <span className="badge bg-primary">{t('training:new')}</span> :
                                                course?.status === CourseStatus.Pass ? <span className="badge bg-success">{t('training:passed')}</span> :
                                                    course?.status === CourseStatus.InProgress ? <span className="badge bg-info">{t('training:inProgress')}</span> :
                                                        course?.status === CourseStatus.Expired ? <span className="badge bg-warning">{t('training:expired')}</span> :
                                                        <></>}
                                        </td>
                                    </tr>
									{course?.status !== CourseStatus.New ?
                                        <>
                                            <tr>
                                                <th>{t('training:startDate')}</th>
                                                <td>
                                                    {course?.startedOn != null ? new Date(course.startedOn + "Z").toLocaleString() : ""}
                                                </td>
                                            </tr>
                                            {course?.completedOn != null ? <>
                                                <tr>
                                                    <th>{t('training:completionDate')}</th>
                                                    <td>
                                                        {new Date(course.completedOn + "Z").toLocaleString()}
                                                    </td>
                                                </tr>
											    <tr>
                                                    <th>{t('training:grade')}</th>
												    <td>
													    {course?.grade}%
												    </td>
                                                </tr></>
                                            : <></>}
										</>
										: <></>}
								</tbody>
                            </table>

                            {course?.prerequisiteId && !course.prerequisiteComplete ? <div className="alert alert-warning alert-outline-coloured mb-0">
                                <div className="alert-icon d-flex align-items-center"><FontAwesomeIcon icon="info-circle"></FontAwesomeIcon></div>
                                <div className="alert-message">
                                    <strong>{t('training:prerequisiteIncomplete')}</strong>
                                    <div>{course.prerequisiteName}</div>
                                </div>
                            </div> : <></>}

                            {course?.status === CourseStatus.InProgress ?
                                <div>
                                    <button type="button" className="btn btn-primary w-100 mt-2 " onClick={resumeCourse}>{t('training:resumeCourse')}</button>
                                </div> :
                                <button type="button" className={"btn btn-primary w-100 mt-2 " + (!course?.prerequisiteComplete ? "disabled" : "")} onClick={startCourse}>{course?.status === CourseStatus.New ? t('training:startCourse') : t('training:restartCourse')}</button>}
						</div>
					</div>
				</div>
            </div>

            <Modal show={showCourseWarning} onHide={() => setShowCourseWarning(false)}>
                <Modal.Header>
                    <Modal.Title>{t('training:warning')}</Modal.Title>
                    <Modal.Body>
                        {t('training:courseInProgressWarning')}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowCourseWarning(false)}>{t('common:no')}</Button>
                        <Button variant="primary" onClick={startCourse}>{t('common:no')}</Button>
                    </Modal.Footer>
                </Modal.Header>
            </Modal>
		</>
	)
}